import { useState } from "react";
import { DashboardLayout } from "@/components/Dashboard/DashboardLayout";
import { useLanguage } from "@/contexts/LanguageContext";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Plus,
  Search,
  Settings2,
  Clock,
  Wrench,
  Box,
  MoreVertical,
  Pencil,
  Trash2,
  GripVertical,
  Play,
  Pause,
  Activity,
  List,
} from "lucide-react";
import { useQuery, useQueryClient, useMutation } from "@tanstack/react-query";
import { api } from "@/lib/apiClient";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import { ProcessFormDialog } from "@/components/Processes/ProcessFormDialog";
import { ProcessMonitoringDashboard } from "@/components/Processes/ProcessMonitoringDashboard";
import { cn } from "@/lib/utils";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";

interface ManufacturingProcess {
  id: string;
  name: string;
  code: string | null;
  description: string | null;
  estimated_duration_minutes: number | null;
  sequence_order: number;
  requires_machine: boolean;
  requires_mold: boolean;
  is_active: boolean;
  created_at: string;
}

export default function ManufacturingProcesses() {
  const { t } = useLanguage();
  const { user, userRole } = useAuth();
  const queryClient = useQueryClient();
  const [searchTerm, setSearchTerm] = useState("");
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [editProcess, setEditProcess] = useState<ManufacturingProcess | null>(null);
  const [deleteProcess, setDeleteProcess] = useState<ManufacturingProcess | null>(null);
  const [activeTab, setActiveTab] = useState("monitoring");

  const canManage = userRole === 'admin' || userRole === 'supervisor';

  // Fetch processes
  const { data: processes, isLoading } = useQuery({
    queryKey: ["manufacturing-processes"],
    queryFn: async () => {
      const { data, error } = await api
        .from("manufacturing_processes")
        .select("*")
        .order("sequence_order", { ascending: true });

      if (error) throw error;
      return data as ManufacturingProcess[];
    },
    enabled: !!user,
  });

  // Fetch active process stats
  const { data: processStats } = useQuery({
    queryKey: ["process-stats"],
    queryFn: async () => {
      const { data, error } = await api
        .from("process_records")
        .select("process_id, status")
        .in("status", ["pending", "in_progress", "paused"]);

      if (error) throw error;

      const stats: Record<string, { pending: number; in_progress: number; paused: number }> = {};
      data?.forEach((record: any) => {
        if (!stats[record.process_id]) {
          stats[record.process_id] = { pending: 0, in_progress: 0, paused: 0 };
        }
        stats[record.process_id][record.status as keyof typeof stats[string]]++;
      });
      return stats;
    },
    enabled: !!user,
  });

  // Delete mutation
  const deleteMutation = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await api
        .from("manufacturing_processes")
        .update({ is_active: false })
        .eq("id", id);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["manufacturing-processes"] });
      toast.success("Proceso desactivado correctamente");
      setDeleteProcess(null);
    },
    onError: (error: any) => {
      toast.error(error.message || "Error al desactivar el proceso");
    },
  });

  // Filter processes
  const filteredProcesses = processes?.filter((process) => {
    const matchesSearch =
      searchTerm === "" ||
      process.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      process.code?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      process.description?.toLowerCase().includes(searchTerm.toLowerCase());

    return matchesSearch && process.is_active;
  });

  // Calculate totals
  const totalStats = {
    total: filteredProcesses?.length || 0,
    withMachine: filteredProcesses?.filter((p) => p.requires_machine).length || 0,
    withMold: filteredProcesses?.filter((p) => p.requires_mold).length || 0,
    active: Object.values(processStats || {}).reduce(
      (sum, stat) => sum + stat.in_progress,
      0
    ),
  };

  const formatDuration = (minutes: number | null) => {
    if (!minutes) return "-";
    if (minutes < 60) return `${minutes} min`;
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    return mins > 0 ? `${hours}h ${mins}m` : `${hours}h`;
  };

  return (
    <DashboardLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-foreground">
              Procesos de Fabricaci&oacute;n
            </h1>
            <p className="text-muted-foreground mt-1">
              Gestiona los procesos de manufactura y sus configuraciones
            </p>
          </div>
          {canManage && (
            <Button onClick={() => setCreateDialogOpen(true)}>
              <Plus className="w-4 h-4 mr-2" />
              Nuevo Proceso
            </Button>
          )}
        </div>

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="grid w-full max-w-md grid-cols-2">
            <TabsTrigger value="monitoring" className="flex items-center gap-2">
              <Activity className="w-4 h-4" />
              Monitoreo
            </TabsTrigger>
            <TabsTrigger value="config" className="flex items-center gap-2">
              <List className="w-4 h-4" />
              Configuraci&oacute;n
            </TabsTrigger>
          </TabsList>

          {/* Monitoring Tab */}
          <TabsContent value="monitoring" className="mt-6">
            <ProcessMonitoringDashboard />
          </TabsContent>

          {/* Configuration Tab */}
          <TabsContent value="config" className="mt-6 space-y-6">
            {/* Stats Cards */}
            <div className="grid gap-4 md:grid-cols-4">
              <Card>
                <CardContent className="pt-4">
                  <div className="flex items-center gap-3">
                    <div className="p-2 rounded-lg bg-blue-500/10">
                      <Settings2 className="w-5 h-5 text-blue-500" />
                    </div>
                    <div>
                      <p className="text-2xl font-bold">{totalStats.total}</p>
                      <p className="text-sm text-muted-foreground">Total Procesos</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="pt-4">
                  <div className="flex items-center gap-3">
                    <div className="p-2 rounded-lg bg-orange-500/10">
                      <Wrench className="w-5 h-5 text-orange-500" />
                    </div>
                    <div>
                      <p className="text-2xl font-bold">{totalStats.withMachine}</p>
                      <p className="text-sm text-muted-foreground">Requieren M&aacute;quina</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="pt-4">
                  <div className="flex items-center gap-3">
                    <div className="p-2 rounded-lg bg-purple-500/10">
                      <Box className="w-5 h-5 text-purple-500" />
                    </div>
                    <div>
                      <p className="text-2xl font-bold">{totalStats.withMold}</p>
                      <p className="text-sm text-muted-foreground">Requieren Molde</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="pt-4">
                  <div className="flex items-center gap-3">
                    <div className="p-2 rounded-lg bg-green-500/10">
                      <Play className="w-5 h-5 text-green-500" />
                    </div>
                    <div>
                      <p className="text-2xl font-bold">{totalStats.active}</p>
                      <p className="text-sm text-muted-foreground">En Ejecuci&oacute;n</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Search */}
            <div className="flex items-center gap-3">
              <div className="relative flex-1 max-w-sm">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <Input
                  placeholder="Buscar por nombre, c&oacute;digo o descripci&oacute;n..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-9"
                />
              </div>
            </div>

            {/* Process List */}
            {isLoading ? (
              <div className="text-center py-12">
                <p className="text-muted-foreground">{t.common.loading}</p>
              </div>
            ) : filteredProcesses && filteredProcesses.length > 0 ? (
              <div className="space-y-3">
                {filteredProcesses.map((process) => {
                  const stats = processStats?.[process.id] || { pending: 0, in_progress: 0, paused: 0 };
                  const hasActiveRecords = stats.pending + stats.in_progress + stats.paused > 0;

                  return (
                    <Card
                      key={process.id}
                      className={cn(
                        "transition-all hover:shadow-md",
                        hasActiveRecords && "border-l-4 border-l-green-500"
                      )}
                    >
                      <CardContent className="p-4">
                        <div className="flex items-center gap-4">
                          {/* Drag Handle & Order */}
                          <div className="flex items-center gap-2 text-muted-foreground">
                            <GripVertical className="w-4 h-4 cursor-grab" />
                            <span className="text-sm font-mono w-6">{process.sequence_order}</span>
                          </div>

                          {/* Main Info */}
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2">
                              <h3 className="font-semibold text-lg truncate">
                                {process.name}
                              </h3>
                              {process.code && (
                                <Badge variant="outline" className="text-xs">
                                  {process.code}
                                </Badge>
                              )}
                            </div>
                            {process.description && (
                              <p className="text-sm text-muted-foreground truncate mt-1">
                                {process.description}
                              </p>
                            )}
                          </div>

                          {/* Duration */}
                          <div className="flex items-center gap-2 text-muted-foreground">
                            <Clock className="w-4 h-4" />
                            <span className="text-sm">
                              {formatDuration(process.estimated_duration_minutes)}
                            </span>
                          </div>

                          {/* Requirements */}
                          <div className="flex items-center gap-2">
                            {process.requires_machine && (
                              <Badge variant="secondary" className="gap-1">
                                <Wrench className="w-3 h-3" />
                                M&aacute;quina
                              </Badge>
                            )}
                            {process.requires_mold && (
                              <Badge variant="secondary" className="gap-1">
                                <Box className="w-3 h-3" />
                                Molde
                              </Badge>
                            )}
                          </div>

                          {/* Active Stats */}
                          {hasActiveRecords && (
                            <div className="flex items-center gap-3 text-xs">
                              {stats.pending > 0 && (
                                <div className="flex items-center gap-1 text-yellow-600">
                                  <Clock className="w-3 h-3" />
                                  {stats.pending}
                                </div>
                              )}
                              {stats.in_progress > 0 && (
                                <div className="flex items-center gap-1 text-blue-600">
                                  <Play className="w-3 h-3" />
                                  {stats.in_progress}
                                </div>
                              )}
                              {stats.paused > 0 && (
                                <div className="flex items-center gap-1 text-orange-600">
                                  <Pause className="w-3 h-3" />
                                  {stats.paused}
                                </div>
                              )}
                            </div>
                          )}

                          {/* Actions */}
                          {canManage && (
                            <DropdownMenu>
                              <DropdownMenuTrigger asChild>
                                <Button variant="ghost" size="icon">
                                  <MoreVertical className="w-4 h-4" />
                                </Button>
                              </DropdownMenuTrigger>
                              <DropdownMenuContent align="end">
                                <DropdownMenuItem onClick={() => setEditProcess(process)}>
                                  <Pencil className="w-4 h-4 mr-2" />
                                  Editar
                                </DropdownMenuItem>
                                <DropdownMenuItem
                                  onClick={() => setDeleteProcess(process)}
                                  className="text-destructive"
                                >
                                  <Trash2 className="w-4 h-4 mr-2" />
                                  Desactivar
                                </DropdownMenuItem>
                              </DropdownMenuContent>
                            </DropdownMenu>
                          )}
                        </div>
                      </CardContent>
                    </Card>
                  );
                })}
              </div>
            ) : (
              <Card>
                <CardContent className="flex flex-col items-center justify-center py-12">
                  <Settings2 className="w-12 h-12 text-muted-foreground mb-4" />
                  <p className="text-muted-foreground mb-2">
                    {searchTerm
                      ? "No se encontraron procesos con ese criterio"
                      : "No hay procesos de fabricaci&oacute;n configurados"}
                  </p>
                  <p className="text-sm text-muted-foreground mb-6">
                    Los procesos definen las etapas de producci&oacute;n de tus productos
                  </p>
                  {canManage && (
                    <Button onClick={() => setCreateDialogOpen(true)}>
                      <Plus className="w-4 h-4 mr-2" />
                      Crear Primer Proceso
                    </Button>
                  )}
                </CardContent>
              </Card>
            )}
          </TabsContent>
        </Tabs>
      </div>

      {/* Create/Edit Dialog */}
      <ProcessFormDialog
        open={createDialogOpen || !!editProcess}
        onOpenChange={(open) => {
          if (!open) {
            setCreateDialogOpen(false);
            setEditProcess(null);
          }
        }}
        process={editProcess}
      />

      {/* Delete Confirmation */}
      <AlertDialog open={!!deleteProcess} onOpenChange={() => setDeleteProcess(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Desactivar proceso?</AlertDialogTitle>
            <AlertDialogDescription>
              El proceso &quot;{deleteProcess?.name}&quot; ser&aacute; desactivado y no aparecer&aacute; en
              las listas de selecci&oacute;n. Los registros hist&oacute;ricos se mantendr&aacute;n.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => deleteProcess && deleteMutation.mutate(deleteProcess.id)}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Desactivar
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </DashboardLayout>
  );
}
